import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { BUSINESS } from "@/lib/business";

const NAV = [
  { href: "#services", label: "Services" },
  { href: "#about", label: "About" },
  { href: "#areas", label: "Service Areas" },
  { href: "#reviews", label: "Reviews" },
  { href: "#contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <a href="#top" data-testid="header-logo" className="flex items-center gap-3 group">
          <img
            src="/logos/optima-logo.png"
            alt="Optima Electrical Solutions Ltd"
            className="h-12 sm:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-[#A0AAB2] hover:text-white transition-colors duration-200 tracking-wide"
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href={`https://wa.me/447511625975`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="flex items-center justify-center w-10 h-10 border border-white/15 hover:border-[#25D366] hover:bg-[#25D366]/10 text-white transition-all duration-300"
            aria-label="WhatsApp us"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#25D366]"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413"/></svg>
          </a>
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            data-testid="header-call-button"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#1E90FF] hover:bg-[#0073E6] text-white text-sm font-medium transition-all duration-300 group"
          >
            <Phone className="w-4 h-4" strokeWidth={2} />
            <span>{BUSINESS.phone}</span>
          </a>
        </div>

        {/* Mobile menu */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-white/5 bg-[#0A0A0A]/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-base text-[#A0AAB2] hover:text-white transition-colors py-2 border-b border-white/5"
              >
                {n.label}
              </a>
            ))}
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="mobile-call-button"
              className="mt-2 flex items-center justify-center gap-2 px-5 py-3 bg-[#1E90FF] text-white text-sm font-medium"
            >
              <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
